import { useEffect, useRef, type ReactNode } from "react";

interface SheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly label: string;
}

/**
 * A sheet: the same paper, risen from the foot of a phone screen, or a
 * centred panel on a desktop. Built on <dialog> so focus, Escape and the
 * backdrop come from the platform.
 */
export function Sheet({ open, onClose, children, label }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // Older engines (and jsdom) lack showModal; the sheet still opens, just
    // without the platform's focus trap.
    const modal = typeof dialog.showModal === "function";
    if (open && !dialog.open) {
      if (modal) dialog.showModal();
      else dialog.setAttribute("open", "");
    }
    if (!open && dialog.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label={label}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      className="fixed inset-x-0 bottom-0 top-auto m-0 w-full max-w-none bg-transparent p-0 backdrop:bg-ink/40 md:inset-0 md:m-auto md:h-fit md:w-[440px]"
    >
      <div className="bg-paper px-gutter pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-6 text-ink md:border md:border-rule md:px-7 md:pb-7">
        {children}
      </div>
    </dialog>
  );
}
